import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import styles from './LoginSignup.module.css';
import axios from 'axios';
import { URL } from '../AxiosUrlGlobal';
import Logo from '../../img/logo.png';
import facebookPixel from '../../utils/facebookPixel';
import { clearTeamSession } from '../Helper/teamAccess';

/* ─── Inline icons (lucide-style strokes, matching dashboard) ─── */
const EyeIcon = ({ open }) => open ? (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
    </svg>
) : (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
        <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
);

const ArrowRight = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="5" y1="12" x2="19" y2="12" />
        <polyline points="12 5 19 12 12 19" />
    </svg>
);

const ZapIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
);

const FraudShieldIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        <line x1="12" y1="8" x2="12" y2="13" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
);

const ShieldIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        <polyline points="9 12 11 14 15 10" />
    </svg>
);

const MailIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="4" width="20" height="16" rx="2" />
        <polyline points="22 6 12 13 2 6" />
    </svg>
);

const StarIcon = () => (
    <svg viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>
);

const PhoneIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.13.96.37 1.9.72 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.91.35 1.85.59 2.81.72A2 2 0 0122 16.92z"/>
    </svg>
);

/**
 * Normalize any Bangla digits the user might type/paste into Latin
 * digits so the API always receives a clean ASCII numeric string,
 * regardless of keyboard / IME / browser locale.
 */
const toLatinDigits = (s = '') =>
    String(s).replace(/[\u09e6-\u09ef]/g, (d) => String(d.charCodeAt(0) - 0x09e6));

const features = [
    { Icon: ZapIcon, title: 'সুপার ফাস্ট BDIX, আনলিমিটেড ট্রাফিক', desc: 'বাংলাদেশের লোকাল হোস্টিং, কোনো ব্যান্ডউইথ লিমিট নেই' },
    { Icon: FraudShieldIcon, title: 'ফ্রড কাস্টমার ডিটেকশন', desc: 'অর্ডার শিপ করার আগেই পাঠাও ও স্টেডফাস্টে ক্যান্সেল রেট চেক' },
    { Icon: MailIcon, title: 'বিজনেস ইমেইল', desc: 'you@yourdomain.com — ইনবক্স, অটো-রিপ্লাই ও ট্র্যাকিং সহ' },
    { Icon: ShieldIcon, title: 'ফ্রি SSL ও কাস্টম ডোমেইন', desc: 'এক ক্লিকে নিজের ডোমেইন কানেক্ট করুন HTTPS সহ' },
];

function Login() {
    const navigate = useNavigate();
    const location = useLocation();

    const queryParams = new URLSearchParams(location.search);
    const initialTab = queryParams.get('tab') || 'login';
    // Plan chosen on the landing pricing card → trial of THAT package.
    const [selectedPlan] = useState(queryParams.get('plan') || '');

    const [signup, setSignup] = useState(initialTab === 'signup');
    const [login, setLogin] = useState(initialTab === 'login');
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const [SignupVerify, setSignupVerify] = useState(false);
    const [SignupLoading, setSignupLoading] = useState(false);
    const [VerifyCode, setVerifyCode] = useState('');
    const [user_id, setUserID] = useState(null);
    const [ResendLoading, setResendLoading] = useState(false);

    const [LoginLoading, setLoginLoading] = useState(false);
    const [lMobile, setLMobile] = useState('');
    const [lpass, setLpass] = useState('');

    const [mobile, setMobile] = useState('');
    const [password, setPassword] = useState('');
    const [username, setUserName] = useState('');

    const [showLoginPw, setShowLoginPw] = useState(false);
    const [showSignupPw, setShowSignupPw] = useState(false);

    const handleTabChange = (tab) => {
        if (tab === 'login') {
            setSignup(false);
            setLogin(true);
            navigate('?tab=login');
        } else if (tab === 'signup') {
            setSignup(true);
            setLogin(false);
            navigate('?tab=signup');
        }
        setSignupVerify(false);
    };

    const handleLogin = async () => {
        setLoginLoading(true);
        axios
            .post(`${URL}login`, { lMobile, lpass })
            .then((res) => {
                console.log('Login Response:', res.data);
                setLoginLoading(false);

                if (res.data.message === 'Password not match') {
                    toast.error('পাসওয়ার্ড মেলেনি');
                } else if (res.data.message === 'User not found') {
                    toast.error('ইউজার পাওয়া যায়নি');
                } else if (res.data.refreshToken) {
                    localStorage.setItem('token', res.data.refreshToken);
                    clearTeamSession();
                    localStorage.setItem('id', res.data.UserId);
                    localStorage.setItem('username', res.data.username || 'Admin');
                    localStorage.setItem('IsSuper', res.data.IsSuper);
                    localStorage.setItem('IsTeam', res.data.IsTeam);
                    localStorage.setItem('CreateStore', res.data.CreateStore);
                    localStorage.setItem('role', res.data.Role);

                    if (res.data.Role === 1) {
                        window.location.href = '/super-admin-dashboard';
                    } else if (res.data.Role === 0 || res.data.Role === 2) {
                        const userId = res.data.UserId;
                        const createStore = res.data.CreateStore;
                        const needsStore =
                            createStore === 'false' || createStore === false || createStore === 'False' || createStore === 'FALSE';
                        // Offer-aware routing (e.g. arriving from an SMS link in a
                        // fresh browser): paid → dashboard; unpaid + active offer →
                        // /get-started; unpaid + expired → /billing.
                        axios
                            .get(`${URL}get-subscription/${userId}`, {
                                headers: { token: res.data.refreshToken },
                            })
                            .then((sub) => {
                                const d = sub.data?.data || sub.data || {};
                                const isPaid = d.IsPaid === true;
                                const offerActive = d.offerActive === true;
                                localStorage.setItem('IsPaid', String(isPaid));
                                localStorage.setItem('offerActive', String(offerActive));
                                if (isPaid) {
                                    window.location.href = needsStore ? `/set-store-name/${userId}` : '/dashboard';
                                } else if (offerActive) {
                                    window.location.href = '/get-started';
                                } else {
                                    window.location.href = '/billing';
                                }
                            })
                            .catch(() => {
                                window.location.href = needsStore ? `/set-store-name/${userId}` : '/dashboard';
                            });
                    }
                }
            })
            .catch((err) => {
                setLoginLoading(false);
                const errorMessage =
                    err.response?.data ||
                    err.message ||
                    'লগইনে সমস্যা হয়েছে';
                toast.error(errorMessage);
            });
    };

    const handleSignup = async () => {
        setSignupLoading(true);

        if (!mobile || !password || !username) {
            toast.error('নাম, মোবাইল নম্বর ও পাসওয়ার্ড দিন');
            setSignupLoading(false);
            return;
        }
        if (mobile.length !== 11) {
            toast.error('১১ সংখ্যার মোবাইল নম্বর লিখুন');
            setSignupLoading(false);
            return;
        }
        axios
            .post(`${URL}signup`, { mobile, password, username, packageType: selectedPlan })
            .then((res) => {
                console.log('Signup Response:', res.data);
                setSignupLoading(false);
                if (res.data === 'User already exists') {
                    toast.error('এই মোবাইল নম্বরে ইতিমধ্যে অ্যাকাউন্ট আছে');
                } else if (res.data.error) {
                    toast.error(res.data.error);
                } else if (res.data.refreshToken) {
                    localStorage.setItem('token', res.data.refreshToken);
                    clearTeamSession();
                    localStorage.setItem('id', res.data.UserId);
                    localStorage.setItem('IsSuper', res.data.IsSuper);
                    localStorage.setItem('IsTeam', res.data.IsTeam);
                    localStorage.setItem('CreateStore', 'false');
                    localStorage.setItem('role', res.data.Role || 0);

                    setSignup(false);
                    setSignupVerify(true);
                    setUserID(res.data.UserId);
                    toast.success('সাইন আপ সফল! এখন OTP ভেরিফাই করুন');
                }
            })
            .catch((err) => {
                setSignupLoading(false);
                const errorMessage =
                    err.response?.data ||
                    err.message ||
                    'সাইন আপে সমস্যা হয়েছে';
                toast.error(errorMessage);
            });
    };

    const handleOtpVerify = async () => {
        if (!VerifyCode) {
            toast.error('ভেরিফিকেশন কোড দিন');
            return;
        }

        setLoginLoading(true);
        axios
            .post(`${URL}signup-otp/validate`, {
                userId: user_id,
                otp: VerifyCode,
            })
            .then((res) => {
                setLoginLoading(false);
                if (res.data === 'OTP is valid') {
                    toast.success('OTP সফলভাবে ভেরিফাই হয়েছে!');

                    facebookPixel.trackCompleteRegistration({
                        content_name: 'User Registration',
                        status: 'verified',
                        value: 1,
                        currency: 'BDT',
                    });

                    // Free trial removed: a freshly verified account is unpaid,
                    // so send them to the launch-offer page to purchase before
                    // any store creation. The paid gate handles the rest.
                    localStorage.setItem('IsPaid', 'false');
                    localStorage.setItem('offerActive', 'true');
                    window.location.href = '/get-started';
                } else if (res.data === 'OTP not found') {
                    toast.error('OTP পাওয়া যায়নি');
                } else if (res.data === 'OTP has expired') {
                    toast.error('OTP মেয়াদোত্তীর্ণ');
                } else if (res.data === 'OTP is invalid') {
                    toast.error('OTP ভুল');
                }
            })
            .catch((err) => {
                setLoginLoading(false);
                const errorMessage =
                    err.response?.data ||
                    err.message ||
                    'OTP ভেরিফাইয়ে সমস্যা হয়েছে';
                toast.error(errorMessage);
            });
    };

    const handleResendOTP = async () => {
        if (!user_id) {
            toast.error('ইউজার ID পাওয়া যায়নি। আবার সাইন আপ করুন।');
            return;
        }

        setResendLoading(true);
        axios
            .post(`${URL}otp/generate/${user_id}`)
            .then((res) => {
                setResendLoading(false);
                if (res.data.includes('wait')) {
                    toast.warning('আবার OTP পাঠানোর আগে ২ মিনিট অপেক্ষা করুন');
                } else {
                    toast.success('আপনার মোবাইলে OTP আবার পাঠানো হয়েছে!');
                }
            })
            .catch((err) => {
                setResendLoading(false);
                const errorMessage =
                    err.response?.data || err.message || 'OTP পাঠাতে সমস্যা হয়েছে';
                toast.error(errorMessage);
            });
    };

    useEffect(() => {
        const options = { autoConfig: true, debug: false };
        const fbPixelId = process.env.REACT_APP_FB_PIXEL_ID || '1398711768516449';
        facebookPixel.init(fbPixelId, options);
        facebookPixel.pageView();
    }, []);

    const handleKeyDown = (e, action) => {
        if (e.key === 'Enter') action();
    };

    return (
        <div className={styles['login-container']} lang="bn">
            <ToastContainer />

            {/* ─── Mobile-only top header ─────────────────────────── */}
            <div className={styles['header-login']}>
                <Link to="/" className={styles['nav-brand']}>
                    <span className={styles['nav-brand-logo']}>
                        <img src={`${process.env.PUBLIC_URL}/logo512.png`} alt="ইকমটেক" />
                    </span>
                    ইকমটেক
                </Link>
                <div className={styles['login-main']}>
                    <ul>
                        <li onClick={() => navigate('/teamlogin')}>টিম লগইন</li>
                        <li onClick={() => handleTabChange('signup')}>সাইন আপ</li>
                        <li onClick={() => handleTabChange('login')}>লগইন</li>
                    </ul>
                </div>
                <div
                    className={styles['mobile-menu-icon']}
                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                >
                    <span></span><span></span><span></span>
                </div>
                {mobileMenuOpen && (
                    <div className={styles['mobile-menu']}>
                        <div
                            className={styles['mobile-menu-close']}
                            onClick={() => setMobileMenuOpen(false)}
                        >×</div>
                        <ul>
                            <li onClick={() => { navigate('/teamlogin'); setMobileMenuOpen(false); }}>টিম লগইন</li>
                            <li onClick={() => { handleTabChange('signup'); setMobileMenuOpen(false); }}>সাইন আপ</li>
                            <li onClick={() => { handleTabChange('login'); setMobileMenuOpen(false); }}>লগইন</li>
                        </ul>
                    </div>
                )}
            </div>

            {/* ─── Two-column layout (form 60% LEFT, branding 40% RIGHT) ──── */}
            <div className={styles['login-signup-main']}>

                {/* RIGHT — branding pane */}
                <aside className={styles['marketing-pane']}>
                    <div className={styles['marketing-top']}>
                        <Link to="/" className={styles['marketing-brand']}>
                            <span className={styles['marketing-brand-dot']}>
                                <img src={`${process.env.PUBLIC_URL}/logo512.png`} alt="ইকমটেক" />
                            </span>
                            <span className={styles['marketing-brand-name']}>ইকমটেক</span>
                        </Link>
                    </div>

                    <div className={styles['marketing-body']}>
                        <div>
                            <p className={styles['marketing-eyebrow']}>
                                বাংলাদেশের কমার্স প্ল্যাটফর্ম
                            </p>
                            <h2 className={styles['marketing-headline']}>
                                এমন স্টোর তৈরি করুন যা
                                <span className={styles.grad}>আপনার ব্যবসা বাড়ায়</span>
                            </h2>
                            <p className={styles['marketing-sub']}>
                                প্রোডাক্ট আপলোড থেকে অর্ডার ডেলিভারি — বাংলাদেশের জন্য
                                তৈরি একটাই ড্যাশবোর্ডে যা যা দরকার সবই পাবেন।
                            </p>
                        </div>

                        <ul className={styles['feature-list']}>
                            {features.map(({ Icon, title, desc }) => (
                                <li key={title} className={styles['feature-item']}>
                                    <span className={styles['feature-icon']}><Icon /></span>
                                    <div className={styles['feature-text']}>
                                        <p className={styles['feature-title']}>{title}</p>
                                        <p className={styles['feature-desc']}>{desc}</p>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className={styles['marketing-footer']}>
                        <div className={styles['avatar-stack']}>
                            <span>R</span>
                            <span>A</span>
                            <span>S</span>
                            <span>+</span>
                        </div>
                        <div className={styles['social-text']}>
                            <div className={styles['stars-row']}>
                                <StarIcon /><StarIcon /><StarIcon /><StarIcon /><StarIcon />
                                <span className={styles['stars-rating']}>৪.৯/৫</span>
                            </div>
                            <p className={styles['stars-caption']}>
                                ১০,০০০+ বাংলাদেশী সেলারের আস্থা
                            </p>
                        </div>
                    </div>
                </aside>

                {/* LEFT — form pane */}
                <main className={styles['form-pane']}>
                    <div className={styles['form-pane-inner']}>

                        {!SignupVerify && (
                            <div className={styles['form-heading-block']}>
                                <h1 className={styles['form-heading']}>
                                    {login ? 'আবার স্বাগতম' : 'নতুন অ্যাকাউন্ট তৈরি করুন'}
                                </h1>
                                <p className={styles['form-subheading']}>
                                    {login
                                        ? 'আপনার সেলার ড্যাশবোর্ডে প্রবেশ করুন।'
                                        : 'মাত্র কয়েক সেকেন্ডে আপনার অনলাইন স্টোর শুরু করুন।'}
                                </p>
                            </div>
                        )}

                        {login && !SignupVerify && (
                            <div className={styles['login-inputs']}>
                                <div className={styles['field']}>
                                    <div className={styles['field-label-row']}>
                                        <label className={styles['field-label']}>মোবাইল নম্বর</label>
                                    </div>
                                    <div className={styles['field-input-wrap']}>
                                        <input
                                            type="tel"
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                                            lang="en"
                                            placeholder="01XXXXXXXXX"
                                            onChange={(e) => setLMobile(toLatinDigits(e.target.value))}
                                            value={lMobile}
                                            onKeyDown={(e) => handleKeyDown(e, handleLogin)}
                                        />
                                    </div>
                                </div>

                                <div className={styles['field']}>
                                    <div className={styles['field-label-row']}>
                                        <label className={styles['field-label']}>পাসওয়ার্ড</label>
                                        <button
                                            type="button"
                                            className={styles['field-label-link']}
                                            onClick={() => navigate('/forgot-password')}
                                        >ভুলে গেছেন?</button>
                                    </div>
                                    <div className={styles['field-input-wrap']}>
                                        <input
                                            type={showLoginPw ? 'text' : 'password'}
                                            placeholder="আপনার পাসওয়ার্ড লিখুন"
                                            className={styles['has-toggle']}
                                            onChange={(e) => setLpass(e.target.value)}
                                            value={lpass}
                                            onKeyDown={(e) => handleKeyDown(e, handleLogin)}
                                        />
                                        <button
                                            type="button"
                                            className={styles['eye-toggle']}
                                            onClick={() => setShowLoginPw(!showLoginPw)}
                                            tabIndex={-1}
                                        >
                                            <EyeIcon open={showLoginPw} />
                                        </button>
                                    </div>
                                </div>

                                <button
                                    type="button"
                                    className={styles['login-btn']}
                                    onClick={handleLogin}
                                    disabled={LoginLoading}
                                >
                                    {LoginLoading ? <span className={styles.spinner} /> : null}
                                    {LoginLoading ? 'লগইন হচ্ছে…' : 'লগইন করুন'}
                                    {!LoginLoading && <ArrowRight />}
                                </button>

                                <div className={styles['mode-switch']}>
                                    <p>
                                        ইকমটেকে নতুন?
                                        <button onClick={() => handleTabChange('signup')}>
                                            অ্যাকাউন্ট তৈরি করুন
                                        </button>
                                    </p>
                                </div>
                            </div>
                        )}

                        {signup && !SignupVerify && (
                            <div className={styles['signup-inputs']}>
                                <div className={styles['field']}>
                                    <div className={styles['field-label-row']}>
                                        <label className={styles['field-label']}>পুরো নাম</label>
                                    </div>
                                    <div className={styles['field-input-wrap']}>
                                        <input
                                            type="text"
                                            placeholder="আপনার নাম"
                                            onChange={(e) => setUserName(e.target.value)}
                                            value={username}
                                            onKeyDown={(e) => handleKeyDown(e, handleSignup)}
                                        />
                                    </div>
                                </div>

                                <div className={styles['field']}>
                                    <div className={styles['field-label-row']}>
                                        <label className={styles['field-label']}>মোবাইল নম্বর</label>
                                    </div>
                                    <div className={styles['field-input-wrap']}>
                                        <input
                                            type="tel"
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                                            lang="en"
                                            placeholder="01XXXXXXXXX"
                                            onChange={(e) => setMobile(toLatinDigits(e.target.value))}
                                            value={mobile}
                                            onKeyDown={(e) => handleKeyDown(e, handleSignup)}
                                        />
                                    </div>
                                </div>

                                <div className={styles['field']}>
                                    <div className={styles['field-label-row']}>
                                        <label className={styles['field-label']}>পাসওয়ার্ড</label>
                                    </div>
                                    <div className={styles['field-input-wrap']}>
                                        <input
                                            type={showSignupPw ? 'text' : 'password'}
                                            placeholder="কমপক্ষে ৬ অক্ষর"
                                            className={styles['has-toggle']}
                                            onChange={(e) => setPassword(e.target.value)}
                                            value={password}
                                            onKeyDown={(e) => handleKeyDown(e, handleSignup)}
                                        />
                                        <button
                                            type="button"
                                            className={styles['eye-toggle']}
                                            onClick={() => setShowSignupPw(!showSignupPw)}
                                            tabIndex={-1}
                                        >
                                            <EyeIcon open={showSignupPw} />
                                        </button>
                                    </div>
                                </div>

                                <button
                                    type="button"
                                    className={styles['login-btn']}
                                    onClick={handleSignup}
                                    disabled={SignupLoading}
                                >
                                    {SignupLoading ? <span className={styles.spinner} /> : null}
                                    {SignupLoading ? 'অ্যাকাউন্ট তৈরি হচ্ছে…' : 'অ্যাকাউন্ট তৈরি করুন'}
                                    {!SignupLoading && <ArrowRight />}
                                </button>

                                <div className={styles['mode-switch']}>
                                    <p>
                                        আগে থেকে অ্যাকাউন্ট আছে?
                                        <button onClick={() => handleTabChange('login')}>
                                            লগইন করুন
                                        </button>
                                    </p>
                                </div>
                            </div>
                        )}

                        {SignupVerify && (
                            <div className={styles['otp-verify-section']}>
                                <div className={styles['otp-icon']}>
                                    <PhoneIcon />
                                </div>
                                <h3 className={styles['otp-title']}>আপনার অ্যাকাউন্ট ভেরিফাই করুন</h3>
                                <div className={styles['otp-message']}>
                                    <p>আপনার মোবাইলে একটি ভেরিফিকেশন কোড পাঠানো হয়েছে</p>
                                    <p>কোডটি নিচে লিখুন</p>
                                </div>
                                <input
                                    type="tel"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    lang="en"
                                    placeholder="৬ ডিজিটের কোড লিখুন"
                                    onChange={(e) => setVerifyCode(toLatinDigits(e.target.value))}
                                    value={VerifyCode}
                                    className={styles['otp-input']}
                                    onKeyDown={(e) => handleKeyDown(e, handleOtpVerify)}
                                />
                                <button
                                    type="button"
                                    className={styles['login-btn']}
                                    onClick={handleOtpVerify}
                                    disabled={LoginLoading}
                                >
                                    {LoginLoading ? <span className={styles.spinner} /> : null}
                                    {LoginLoading ? 'ভেরিফাই হচ্ছে…' : 'কোড ভেরিফাই করুন'}
                                    {!LoginLoading && <ArrowRight />}
                                </button>
                                <p
                                    className={styles['resend-text']}
                                    onClick={handleResendOTP}
                                >
                                    কোড পাননি?
                                    <span>{ResendLoading ? 'পাঠানো হচ্ছে…' : 'আবার পাঠান'}</span>
                                </p>
                            </div>
                        )}
                    </div>
                </main>
            </div>

            <div className={styles['footer-login']}>
                <p>© {new Date().getFullYear()} ইকমটেক। সর্বস্বত্ব সংরক্ষিত।</p>
            </div>
        </div>
    );
}

export default Login;
